"use client"

import styled from "@emotion/styled"
import { Accordion, AccordionDetails, AccordionSummary } from "@mui/material"
import { ArrowLeftIcon } from "@mui/x-date-pickers"
import { useMap } from "@vis.gl/react-google-maps"
import Image from "next/image"
import { Dispatch, SetStateAction, useState } from "react"
import { NewPhoneIcon, NewText, PinIcon, useIsDesktop } from "ui"
import { getUmbracoMediaUrl } from "utils/getUmbracoMediaUrl"
import { BranchType, MapData } from "./FactoriesMap"

const StyledListContainer = styled.div`
  position: static;
  padding: 0px 16px;
  background-color: #ffffff;
  ${(p) => p.theme.breakpoints.up("md")} {
    position: absolute;
    z-index: 99;
    top: 40px;
    left: 39.5px;
    padding: 8px 16px;
    border-radius: 20px;
    box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.08);
    max-height: calc(100% - 80.88px);
    overflow: auto;
    max-width: 291px;
  }
`

const StyledAccordionDetails = styled(AccordionDetails)`
  padding: 0;
`

const StyledAccordionSummary = styled(AccordionSummary)`
  width: 100%;
  ${(p) => p.theme.breakpoints.up("md")} {
    width: 251px;
  }
`

const StyledAccordionDetailsItem = styled.div`
  padding: 16px 8px;
  display: flex;
  flex-direction: column;
  gap: 8px;
  border: 0.5px solid #f7f7f7;
  border-left: none;
  border-right: none;
  cursor: pointer;
  :hover {
    background-color: #c1b9b066 !important;
  }
`

const StyledImageBox = styled.div`
  width: 35px;
  height: 35px;
  min-width: 35px;
  min-height: 35px;
  background: white;
  border-radius: 4px;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0px 4px 12px 0px rgba(0, 0, 0, 0.08);
`

const StyledAddressItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
`
const StyledLink = styled.a`
  display: flex;
  gap: 8px;
  text-decoration: none;
  width: fit-content;
`

type Props = {
  branches: MapData
  setLocalSelect: Dispatch<SetStateAction<string>>
  localSelect: string
  setScreenType: Dispatch<SetStateAction<"map" | "list">>
  locale: string
}

const FactoriesList = ({ branches, setLocalSelect, localSelect, setScreenType, locale }: Props) => {
  const isDesktop = useIsDesktop()
  const map = useMap()
  const [activeAccordion, setActiveAccordion] = useState<number | null>(null)
  const isExpanded = ({ index, branch }: { index: number; branch: BranchType }) => {
    return (
      activeAccordion === index ||
      (localSelect &&
        branch.companyFactorys?.find((factory) => factory.branchNumber === localSelect))
    )
  }

  const handleBranchSelection = ({
    factory,
    position,
  }: {
    factory: {
      branchNumber: string
    }
    position: { lng: number; lat: number }
  }) => {
    if (localSelect !== factory.branchNumber) {
      setLocalSelect(factory.branchNumber)
      setScreenType("map")

      // Try to get the info window height from the DOM
      let infoWindowOffset = 0 // default offset

      if (map) {
        if (!isDesktop) {
          map.setCenter(position)
          map.setZoom(12)
        } else {
          // Get map div to calculate pixel-to-lat conversion
          const mapDiv = map.getDiv?.()
          if (mapDiv) {
            const mapHeight = mapDiv.offsetHeight
            const infoHeight = 250
            // Calculate how much latitude corresponds to info window height
            // Get map bounds in lat/lng
            const mapBounds = map.getBounds()
            if (mapBounds) {
              const ne = mapBounds.getNorthEast()
              const sw = mapBounds.getSouthWest()
              const latDiff = Math.abs(ne.lat() - sw.lat())
              // Offset in latitude units
              infoWindowOffset = (infoHeight / mapHeight) * latDiff
            }

            // Calculate new center with offset
            let newCenter = {
              ...position,
              lat: position.lat + infoWindowOffset,
            }
            if (localSelect === "0") {
              newCenter = { ...position, lat: position.lat + 0.062 }
            }

            map.setCenter(newCenter)

            map.setZoom(12)
          }
        }
      }
    }
  }

  return (
    <StyledListContainer>
      {branches?.map((branch, index) => {
        return (
          <Accordion
            key={index}
            style={{
              padding: 0,
              margin: 0,
              boxShadow: "unset",
              border: "none",
              borderRadius: "unset",
              opacity: 1,
            }}
            expanded={isExpanded({ index, branch }) ? true : false}
            sx={{
              "&:before": {
                display: "none",
              },
            }}
            onChange={() => {
              setLocalSelect("0")
              if (isExpanded({ index, branch })) {
                setActiveAccordion(null)
              } else setActiveAccordion(index)
            }}
          >
            <StyledAccordionSummary
              aria-controls="panel1a-content"
              id="panel1a-header"
              style={{
                border: "none",
                borderBottom: !isExpanded({ index, branch }) ? "1px solid #F7F7F7" : "none",
                outline: "none",
                color: "black",
                backgroundColor: "transparent",
                borderRadius: "none",
                height: "61px",
                padding: 0,
                flexDirection: isExpanded({ index, branch }) ? "row-reverse" : "row",
                gap: "4px",
              }}
              expandIcon={
                <ArrowLeftIcon
                  style={{ fill: "black", transform: locale === "en" ? "rotate(180deg)" : "unset" }}
                />
              }
            >
              <NewText
                component={"h2"}
                desktopFontSize={20}
                desktopFontWeight={600}
                smallDesktopLineHeight={"100%"}
                color="black"
              >
                {branch.companyAssociation}
              </NewText>
            </StyledAccordionSummary>
            <StyledAccordionDetails>
              {branch.companyFactorys?.map((factory, key) => {
                const position = {
                  lng: factory?.mapCoordinates?.address?.coordinates?.longitude,
                  lat: factory?.mapCoordinates?.address?.coordinates?.latitude,
                }
                return (
                  <StyledAccordionDetailsItem
                    key={key}
                    style={{
                      backgroundColor: localSelect === factory.branchNumber ? "#C1B9B066" : "white",
                    }}
                    onClick={() => handleBranchSelection({ factory, position })}
                  >
                    <StyledAddressItem>
                      <StyledImageBox>
                        <Image
                          src={getUmbracoMediaUrl(factory.icon?.[0]?.url)}
                          alt={factory.icon?.[0]?.altText || ""}
                          width={22}
                          height={22}
                        />
                      </StyledImageBox>
                      <NewText
                        component={"p"}
                        desktopFontSize={18}
                        desktopFontWeight={400}
                        desktopLineHeight={"100%"}
                        color="black"
                      >
                        {factory.factoryName}
                      </NewText>
                    </StyledAddressItem>
                    {factory?.address?.[0] && (
                      <StyledLink href={factory?.address?.[0]} target="_blank">
                        <PinIcon />
                        <NewText
                          component={"p"}
                          desktopFontSize={16}
                          desktopFontWeight={400}
                          desktopLineHeight={"100%"}
                          color="#2768A3"
                          style={{ textDecoration: "underline" }}
                        >
                          {factory?.mapCoordinates?.address?.street}
                        </NewText>
                      </StyledLink>
                    )}
                    {factory?.phoneNumber?.[0]?.url && (
                      <StyledLink
                        href={factory?.phoneNumber?.[0]?.url}
                        target={factory?.phoneNumber?.[0]?.target}
                        onClick={(e) => {
                          e.stopPropagation()
                        }}
                      >
                        <NewPhoneIcon />
                        <NewText
                          component={"p"}
                          desktopFontSize={16}
                          desktopFontWeight={400}
                          desktopLineHeight={"100%"}
                          color="black"
                        >
                          {factory?.phoneNumber?.[0]?.name}
                        </NewText>
                      </StyledLink>
                    )}
                  </StyledAccordionDetailsItem>
                )
              })}
            </StyledAccordionDetails>
          </Accordion>
        )
      })}
    </StyledListContainer>
  )
}

export default FactoriesList
